import { useState, useRef, useEffect } from 'react';
import { makeStyles, tokens, Button, Tooltip } from '@fluentui/react-components';
import { PlayRegular, DismissRegular, EditRegular, CheckmarkCircleRegular, BotRegular, BotSparkleRegular } from '@fluentui/react-icons';
import { useAgentChatAguiStore } from '@/stores/agentChatAguiStore';
import { ModelSelector } from '@/components/shared/ModelSelector';

/**
 * Shared styles for auto-growing textarea.
 * IMPORTANT: These values must be identical between the textarea and the hidden
 * pseudo-element to ensure accurate height calculation for auto-grow behavior.
 */
const sharedTextareaStyles = {
  padding: tokens.spacingVerticalS,
  paddingInline: tokens.spacingHorizontalS,
  fontFamily: 'inherit',
  fontSize: tokens.fontSizeBase300,
  lineHeight: tokens.lineHeightBase300,
  minHeight: '60px',
  maxHeight: '200px',
  gridArea: '1 / 1 / 2 / 2',
} as const;

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // Unified wrapper for textarea + buttons - appears as single component
  inputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // CSS Grid trick for auto-growing textarea without JS
  textareaWrapper: {
    display: 'grid',
    // Both textarea and ::after occupy the same grid cell
    '&::after': {
      content: 'attr(data-replicated-value) " "',
      whiteSpace: 'pre-wrap',
      visibility: 'hidden',
      overflow: 'hidden',
      // Shared styles for accurate sizing (must match textarea)
      ...sharedTextareaStyles,
    },
  },
  textarea: {
    width: '100%',
    resize: 'none',
    border: 'none',
    borderRadius: '0',
    outline: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    overflowY: 'auto',
    // Shared styles for accurate sizing (must match ::after pseudo-element)
    ...sharedTextareaStyles,
    '&:focus': {
      outline: 'none',
    },
    '&::placeholder': {
      color: tokens.colorNeutralForeground4,
    },
    '&:disabled': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      color: tokens.colorNeutralForegroundDisabled,
    },
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  modeButton: {
    minWidth: 'auto',
    paddingInline: tokens.spacingHorizontalXS,
    '& svg': {
      width: '17px',
      height: '17px',
    },
  },
  leftButtons: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXS,
    alignItems: 'center',
  },
});

interface ChatInputProps {
  onSendMessage: (content: string) => void;
  onStopStreaming: () => void;
  isStreaming: boolean;
  disabled?: boolean;
  /** Custom placeholder text. Defaults to 'Type a message'. */
  placeholder?: string;
  /** Array of previous user messages for history navigation (most recent last) */
  messageHistory?: string[];
  /** Whether the edit scope is full workflow (shows agent mode toggle) */
  isFullWorkflowScope?: boolean;
}

export const ChatInput = ({ onSendMessage, onStopStreaming, isStreaming, disabled, placeholder, messageHistory = [], isFullWorkflowScope = false }: ChatInputProps) => {
  const styles = useStyles();
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Mode states from store
  const autoApplyMode = useAgentChatAguiStore((state) => state.autoApplyMode);
  const setAutoApplyMode = useAgentChatAguiStore((state) => state.setAutoApplyMode);
  const agentMode = useAgentChatAguiStore((state) => state.agentMode);
  const setAgentMode = useAgentChatAguiStore((state) => state.setAgentMode);

  const handleAutoApplyToggle = () => {
    setAutoApplyMode(!autoApplyMode);
  };

  const handleAgentModeToggle = () => {
    setAgentMode(!agentMode);
  };

  // History navigation state
  // -1 means not navigating history (showing current input)
  // 0 is most recent message, 1 is second most recent, etc.
  const [historyIndex, setHistoryIndex] = useState(-1);
  // Save the current input when starting to navigate history
  const [savedInput, setSavedInput] = useState('');

  useEffect(() => {
    // Focus the textarea when the component mounts
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    // Refocus when streaming ends so user can immediately type again
    if (!isStreaming) {
      textareaRef.current?.focus();
    }
  }, [isStreaming]);

  const handleSend = () => {
    if (!input.trim() || isStreaming) return;
    onSendMessage(input.trim());
    setInput('');
    // Reset history navigation state
    setHistoryIndex(-1);
    setSavedInput('');
    // Keep focus in the textarea after sending
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }

    // History navigation with up/down arrows (only when input is empty or already navigating)
    if (e.key === 'ArrowUp' && messageHistory.length > 0) {
      // Only navigate if input is empty or we're already navigating history
      if (input.trim() === '' || historyIndex >= 0) {
        e.preventDefault();

        if (historyIndex === -1) {
          // Starting to navigate history - save current input
          setSavedInput(input);
          // Go to most recent message (index 0 = last item in array)
          setHistoryIndex(0);
          setInput(messageHistory[messageHistory.length - 1] || '');
        } else if (historyIndex < messageHistory.length - 1) {
          // Go to older message
          const newIndex = historyIndex + 1;
          setHistoryIndex(newIndex);
          setInput(messageHistory[messageHistory.length - 1 - newIndex] || '');
        }
        // If at oldest message, do nothing
      }
    }

    if (e.key === 'ArrowDown' && historyIndex >= 0) {
      e.preventDefault();

      if (historyIndex === 0) {
        // Back to current input
        setHistoryIndex(-1);
        setInput(savedInput);
        setSavedInput('');
      } else {
        // Go to more recent message
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(messageHistory[messageHistory.length - 1 - newIndex] || '');
      }
    }
  };

  // Reset history navigation when user types manually
  const handleInputChange = (_: unknown, data: { value: string }) => {
    setInput(data.value);
    // If user modifies the input while navigating history, exit history mode
    if (historyIndex >= 0) {
      setHistoryIndex(-1);
      setSavedInput('');
    }
  };

  const effectiveDisabled = !!(disabled || isStreaming);

  return (
    <div className={styles.container}>
      {/* Unified input component with shared rounded corners */}
      <div className={styles.inputWrapper}>
        {/* CSS Grid wrapper - the data attribute mirrors textarea value for auto-grow */}
        <div className={styles.textareaWrapper} data-replicated-value={input}>
          <textarea
            ref={textareaRef}
            className={styles.textarea}
            value={input}
            onChange={(e) => handleInputChange(null, { value: e.target.value })}
            onKeyDown={handleKeyDown}
            placeholder={placeholder ?? 'Type a message'}
            aria-label="Chat message input"
            disabled={effectiveDisabled}
          />
        </div>
        <div className={styles.buttonRow}>
          <div className={styles.leftButtons}>
            {/* Agent mode toggle - only shown when editing full workflow */}
            {isFullWorkflowScope && (
              <Tooltip
                content={agentMode
                  ? 'Agent mode: Creates agentic workflows. Click to switch to classic.'
                  : 'Classic mode: Creates traditional workflows. Click to switch to agent.'}
                relationship="label"
              >
                <Button
                  appearance="subtle"
                  size="small"
                  className={styles.modeButton}
                  icon={agentMode ? <BotSparkleRegular /> : <BotRegular />}
                  onClick={handleAgentModeToggle}
                  aria-label={agentMode ? 'Agent mode enabled' : 'Classic mode enabled'}
                />
              </Tooltip>
            )}

            {/* Edit mode toggle */}
            <Tooltip
              content={autoApplyMode
                ? 'Auto-apply: Edits applied automatically. Click to require approval.'
                : 'Ask first: Edits require approval. Click to auto-apply.'}
              relationship="label"
            >
              <Button
                appearance="subtle"
                size="small"
                className={styles.modeButton}
                icon={autoApplyMode ? <CheckmarkCircleRegular /> : <EditRegular />}
                onClick={handleAutoApplyToggle}
                aria-label={autoApplyMode ? 'Auto-apply mode' : 'Ask first mode'}
              />
            </Tooltip>

            {/* Model selector — shared component handles GitHub auth + dropdown */}
            <ModelSelector disabled={isStreaming} />
          </div>

          {/* Action buttons */}
          {isStreaming ? (
            <Button
              icon={<DismissRegular />}
              onClick={onStopStreaming}
              appearance="secondary"
              size="small"
            >
              Stop
            </Button>
          ) : (
            <Button
              icon={<PlayRegular />}
              onClick={handleSend}
              disabled={Boolean(!input.trim() || disabled)}
              appearance="primary"
              size="small"
            >
              Generate
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
